import React, { createContext, useContext, useMemo, useCallback, useLayoutEffect, useState, useEffect } from 'react';

const THEME_STORAGE_KEY = 'faya-theme';

/** @type {'light' | 'dark' | 'system'} */
const getStoredTheme = () => {
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    if (stored === 'light' || stored === 'dark' || stored === 'system') return stored;
  } catch (_) {}
  return 'system';
};

/** @type {'light' | 'dark'} */
const getSystemTheme = () => {
  if (typeof window === 'undefined') return 'light';
  try {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  } catch (_) {
    return 'light';
  }
};

/** @param {'light' | 'dark' | 'system'} theme */
const getResolvedTheme = (theme) => {
  return theme === 'system' ? getSystemTheme() : theme;
};

const ThemeContext = createContext(null);

export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(getStoredTheme);
  const [systemDark, setSystemDark] = useState(() => {
    if (typeof window === 'undefined') return false;
    try {
      return window.matchMedia('(prefers-color-scheme: dark)').matches;
    } catch (_) {
      return false;
    }
  });

  const resolvedTheme = useMemo(
    () => (theme === 'system' ? (systemDark ? 'dark' : 'light') : theme),
    [theme, systemDark]
  );

  // Apply theme to document (no reload) with smooth transition
  useLayoutEffect(() => {
    const root = document.documentElement;
    root.setAttribute('data-theme', resolvedTheme);
    root.style.setProperty('color-scheme', resolvedTheme);
  }, [resolvedTheme]);

  // Listen to system preference when theme is 'system'
  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e) => setSystemDark(e.matches);
    mq.addEventListener('change', handleChange);
    setSystemDark(mq.matches);
    return () => mq.removeEventListener('change', handleChange);
  }, []);

  const setTheme = useCallback((value) => {
    const next = value === 'light' || value === 'dark' || value === 'system' ? value : 'system';
    setThemeState(next);
    try {
      localStorage.setItem(THEME_STORAGE_KEY, next);
    } catch (_) {}
  }, []);

  const toggleTheme = useCallback(() => {
    const next = resolvedTheme === 'dark' ? 'light' : 'dark';
    setTheme(next);
  }, [resolvedTheme, setTheme]);

  const value = useMemo(
    () => ({
      theme,
      resolvedTheme,
      setTheme,
      toggleTheme,
      isDark: resolvedTheme === 'dark',
      isLight: resolvedTheme === 'light',
    }),
    [theme, resolvedTheme, setTheme, toggleTheme]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider');
  return ctx;
}

export { THEME_STORAGE_KEY, getResolvedTheme, getSystemTheme };
